import { Box, LinearProgress, linearProgressClasses, Stack, styled, Typography } from '@mui/material'

import { commaSeparated } from '#/shared'
import { formatDate } from '#/shared/lib/helpers/date-helper'
import { formatPlanPrice } from '#/shared/lib/helpers/format-plan-price'
import { declineToken } from '#/shared/lib/helpers/get-token'

import styles from './subscription.module.scss'

const BorderLinearProgress = styled(LinearProgress)(() => ({
	height: 18,
	borderRadius: 6,
	[`&.${linearProgressClasses.colorPrimary}`]: {
		backgroundColor: '#EFF0F2',
	},
	[`& .${linearProgressClasses.bar}`]: {
		borderRadius: 6,
		backgroundColor: '#8280FF',
	},
}))

interface CurrentPlanAndBalanceProps {
	balance: number
	planTokenLimit: string
	planPrice: string
	isIndividual: boolean
	isRecurring: boolean
	nextPaymentAt?: string | null
}

function formatNextPaymentLabel(iso: string | null | undefined): string | null {
	if (!iso?.trim()) return null
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return null
	return formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export const CurrentPlanAndBalance = ({
	balance,
	planTokenLimit,
	planPrice,
	isIndividual,
	isRecurring,
	nextPaymentAt,
}: CurrentPlanAndBalanceProps) => {
	const formatterdPlanPrice = formatPlanPrice(planPrice, isIndividual)
	const isDemoPlan = !formatterdPlanPrice.includes('₽')
	const nextPaymentFormatted = formatNextPaymentLabel(nextPaymentAt)
	const nextPaymentLabel = nextPaymentFormatted
		? `${isRecurring ? 'Следующее списание' : 'Дата окончания'}: ${nextPaymentFormatted}`
		: 'Без даты окончания'
	const planTokenLimitNumber = Number(planTokenLimit)
	const percentage = planTokenLimitNumber > 0 ? Math.min(100, Math.max(0, (balance / planTokenLimitNumber) * 100)) : 0

	return (
		<Stack className={styles.balanceContainer} gap={'20px'}>
			<Box className={`${styles.balanceBox} ${!isIndividual ? styles.balanceBox_corporate : styles.balanceBox_hidden}`}>
				<Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ marginBottom: '15px' }}>
					<Typography
						className={styles.subTitle}
					>
						Моя подписка
					</Typography>
				</Stack>
				<Stack>
					<Typography className={styles.title} sx={{ marginBottom: '10px' }}>
						{commaSeparated(Math.round(Number(planTokenLimit)))} токенов
					</Typography>
					<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} className={styles.planInfoRow}>
						<Typography className={styles.subTitle}>
							{isDemoPlan ? (isIndividual ? 'Индивидуальная' : 'Демо доступ') : `${formatterdPlanPrice} / мес`}
						</Typography>
						<Typography className={styles.subTitle} sx={{ textAlign: 'right' }}>
							{nextPaymentLabel}
						</Typography>
					</Stack>
				</Stack>
			</Box>
			<Box className={styles.balanceBox}>
				<Typography className={styles.subTitle}>Мой баланс</Typography>
				<Box
					sx={{ marginTop: '15px', marginBottom: '10px' }}
					width='100%'
					display='flex'
					alignItems='center'
					justifyContent='space-between'
				>
					<Typography sx={{ fontWeight: 600 }}>{declineToken(balance.toString())}</Typography>
					<Typography className='color-gray'>{Math.round(percentage)} %</Typography>
				</Box>
				<BorderLinearProgress variant='determinate' value={percentage} />
			</Box>
		</Stack>
	)
}
